import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/site/Reveal";
import { PRODUCTS, GROUPS, groupOf } from "@/lib/products";
import { waLink, productWaMsg } from "@/lib/site";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const FILTERS = [
  { slug: "all", name: "All Products" },
  ...GROUPS.filter((g) => g.slug !== "other-fmcg"),
];

export default function CataloguePage() {
  const [params, setParams] = useSearchParams();
  const active = params.get("cat") || "all";

  const list = useMemo(
    () =>
      active === "all"
        ? PRODUCTS
        : PRODUCTS.filter((p) => {
            const g = groupOf(p.category);
            return g?.slug === active || p.category === active;
          }),
    [active]
  );

  return (
    <div className="bg-cream">
      <section className="bg-navy pb-14 pt-32 text-white md:pt-40">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <FadeUp>
            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-gold">
              RBL EXIM — Product Catalogue
            </p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
              Premium Indian food products, ready for your market.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70">
              Spices, superfoods, mango pulp, cooking pastes, pickles and jams.
              Select any product to enquire — pricing and MOQs are shared on
              request.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-12 md:px-10 md:py-16">
        <div data-testid="catalogue-filters" className="flex flex-wrap gap-2.5">
          {FILTERS.map((f) => (
            <button
              key={f.slug}
              onClick={() => setParams(f.slug === "all" ? {} : { cat: f.slug })}
              data-testid={`filter-${f.slug}`}
              className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                active === f.slug
                  ? "border-navy bg-navy text-white"
                  : "border-navy/15 bg-white text-navy/70 hover:border-gold hover:text-navy"
              }`}
            >
              {f.name}
            </button>
          ))}
        </div>

        <div data-testid="catalogue-grid" className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {list.map((p, i) => (
            <FadeUp key={p.slug} delay={Math.min(i, 8) * 0.04}>
              <article
                data-testid={`product-card-${p.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10"
              >
                <div className="overflow-hidden bg-white">
                  <img
                    src={p.image}
                    alt={`${p.name} — RBL EXIM ${p.categoryLabel}`}
                    loading="lazy"
                    className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gold-dark">
                    {p.categoryLabel}
                  </p>
                  <h3 className="mt-1.5 font-display text-lg font-semibold tracking-tight text-navy">
                    {p.name}
                  </h3>
                  {p.brand && (
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-gold-dark">
                      by {p.brand}
                    </p>
                  )}
                  <div className="mt-4 flex flex-col gap-2 pt-2">
                    <Link
                      to={`/?product=${encodeURIComponent(p.name)}&category=${encodeURIComponent(p.categoryLabel)}#enquire`}
                      data-testid={`product-enquire-${p.slug}`}
                      className="flex items-center justify-center gap-1.5 rounded-full bg-navy py-2.5 text-xs font-semibold uppercase tracking-[0.1em] text-white transition-colors duration-300 hover:bg-gold hover:text-navy"
                    >
                      Enquire Now <ArrowUpRight size={14} />
                    </Link>
                    <a
                      href={waLink(productWaMsg(p.name, p.categoryLabel))}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`product-whatsapp-${p.slug}`}
                      className="flex items-center justify-center gap-1.5 rounded-full border border-[#25D366]/50 py-2.5 text-xs font-semibold uppercase tracking-[0.1em] text-[#128C4A] transition-colors duration-300 hover:bg-[#25D366] hover:text-white"
                    >
                      <WhatsAppIcon /> Enquire on WhatsApp
                    </a>
                  </div>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>

        <FadeUp>
          <div className="mt-16 flex flex-col items-center justify-between gap-6 rounded-2xl bg-navy p-8 text-white md:flex-row md:p-12">
            <div>
              <h2 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">
                Need specifications, MOQs or pricing?
              </h2>
              <p className="mt-2 max-w-lg text-sm leading-relaxed text-white/70">
                Tell us what you need and our team will respond with product
                details matched to your market.
              </p>
            </div>
            <Link
              to="/#enquire"
              data-testid="catalogue-quote-cta"
              className="group inline-flex shrink-0 items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-navy transition-colors duration-300 hover:bg-white"
            >
              Request Product Details
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
